/**
 * Raporlar — eski `products_exportexcel.php` (Ürün Kâr Tablosu) ve
 * `products_exportsolidpanelsperproduct.php` (Masif Panel m² raporu) karşılığı.
 *
 * Eski sistemin düzeltilen hataları:
 *  · G-07: Excel raporlarında HİÇ rol kontrolü yoktu. Dosyalar doğrudan URL ile
 *    çağrılıyor, sidebar'da linklenmedikleri için "gizli" sanılıyordu. Kârlılık
 *    tablosu tüm maliyet ve kâr rakamlarını içerdiği hâlde herkese açıktı.
 *    Artık üç kat koruma var: (1) bu ekranda `financeView` kontrolü,
 *    (2) callable guard'ı, (3) Storage'da `reports/finance/` yolu financeView kilitli.
 *  · Rapor dosyası kalıcı ve tahmin edilebilir bir URL'de duruyordu. Artık dosya
 *    yalnızca **15 dakika geçerli imzalı URL** ile indirilir.
 *  · Masif panel raporu parça ID'lerini (120/119/255) koda gömüyordu. Artık
 *    `tags: ["masif-panel"]` etiketi üzerinden yürür.
 */

import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { collection, query, where, orderBy, limit } from "firebase/firestore";
import { PageHeader, Spinner, EmptyState, StatusBadge } from "../components";
import { useCollection, useCallableAction } from "../hooks";
import { db } from "../lib/firebase";
import { useCan } from "../lib/auth";
import { dt, n } from "../lib/format";
import { ErrorNote } from "../components";

export interface ReportDoc {
  id: string;
  type: string;
  /**
   * ★ SUNUCU BU ALANI ARTIK YAZIYOR — eskiden HİÇ yazılmıyordu ve arşivin
   * RAPOR sütunu tamamen boş kalıyordu. Eski kayıtlar için `reportLabel()`
   * `type`/`fileName` üzerinden yedek ad üretir; boş hücre bırakılmaz.
   */
  title?: string;
  /** Görünürlüğü bu dizi belirler; yeni rapor tipi kural değişikliği gerektirmez. */
  allowedRoles: string[];
  status?: "queued" | "running" | "ready" | "error";
  rowCount?: number;
  createdAt?: { toDate: () => Date } | null;
  createdBy?: string;
  errorMessage?: string | null;
  /** Storage yolu — arşivden indirme bunun üzerinden yapılır. */
  path?: string;
  fileName?: string;
  sizeBytes?: number;
  /**
   * Raporun okuduğu maliyetlerin en son hesaplanma anı. Kullanıcı arşiv
   * satırına bakarak "bu dosya hangi tarihli maliyetleri içeriyor" sorusunu
   * dosyayı açmadan yanıtlayabilsin.
   */
  costsCalculatedAt?: { toDate: () => Date } | null;
  staleCostCount?: number;
}

/** Türü bilinen raporların insan okur adı — eski kayıtlarda `title` yok. */
const TYPE_LABEL: Record<string, string> = {
  profit: "Ürün Kâr Tablosu",
  solidPanel: "Masif Panel Raporu",
  auditLog: "Denetim Kaydı",
};

/**
 * Arşiv satırının adı. Sıra: yazılmış başlık → tür karşılığı → dosya adı → tür.
 * ★ ASLA BOŞ DÖNMEZ; boş hücre "kayıt bozuk" gibi okunuyordu.
 */
function reportLabel(r: ReportDoc): string {
  return r.title?.trim() || TYPE_LABEL[r.type] || r.fileName || r.type || "(adsız rapor)";
}

/**
 * Tüm rapor callable'ları aynı sözleşmeyi döner.
 *
 * ★ `empty` DALI: rapor "veri yok" diyebilir ve bu bir HATA DEĞİLDİR. O durumda
 * `url`/`fileName` gelmez; `message` kullanıcıya ne yapması gerektiğini söyler.
 * Bkz. `functions/src/reports/solidPanel.ts`.
 */
export interface ReportOutput {
  /** `true` ise dosya üretilmedi; `message` sebebi yazar. */
  empty?: boolean;
  reason?: string;
  message?: string;
  /** Boş durumda etiketlenmeye aday parçalar. */
  candidates?: Array<{ id: string; name: string }>;
  /**
   * 15 dakika geçerli imzalı indirme adresi.
   *
   * ★ KÜÇÜK RAPORLARDA BOŞTUR. İmzalı URL üretmek `iam.serviceAccounts.signBlob`
   * iznini gerektiriyordu ve bu izin çalışma zamanı hesabında yoktu — her rapor
   * "Beklenmeyen bir hata" ile düşüyordu. Sunucu artık dosyayı `contentBase64`
   * ile yanıtın içinde gönderiyor; ortada paylaşılabilir bir bağlantı hiç
   * oluşmuyor. `url` yalnızca yanıta sığmayan büyük dosyalarda dolu gelir.
   */
  url: string;
  /** Dosyanın kendisi (base64). Doluysa indirme bundan yapılır. */
  contentBase64?: string;
  /**
   * ★ EPOCH MİLİSANİYE (sayı), ISO metin DEĞİL. `Date.parse(sayı)` NaN döndürüp
   * geri sayımı hiç başlatmıyor, etiket "0:00 sonra geçersiz" yazıyordu —
   * kullanıcı geçerli bağlantıyı ölü sanıp indirmiyordu.
   */
  expiresAt: number;
  /** Sunucunun hazır gönderdiği kalan süre; hesaplamaya gerek yok. */
  expiresInSeconds?: number;
  fileName: string;
  rowCount: number;
  /**
   * ★ GÜNCELLİK KANITI (kâr tablosu). Rapor her çağrıda canlı Firestore'dan
   * okur; bu üç alan bunu ÖLÇÜLEBİLİR kılar:
   *  · `generatedAtMs`       — dosyanın basıldığı an
   *  · `costsCalculatedAtMs` — okunan maliyetlerin en son hesaplanma anı
   *  · `staleCostCount`      — fiyatı değişip maliyeti yenilenmemiş ürün sayısı
   * Aynı bilgiler Excel'in "Özet" sayfasına da yazılır.
   */
  generatedAtMs?: number;
  costsCalculatedAtMs?: number;
  staleCostCount?: number;
}

/** Epoch ms → Türkiye saatiyle okunur metin. 0/yok → "—". */
function msToTr(ms: number | undefined): string {
  if (!ms || !Number.isFinite(ms)) return "—";
  return new Date(ms).toLocaleString("tr-TR", { timeZone: "Europe/Istanbul" });
}

/**
 * ★ MASİF PANEL RAPORUNUN ÖN KOŞULU — CANLIDA ÖLÇÜLÜR, SABİT YAZILMAZ.
 *
 * Eskiden uyarı metni "0/312" diye KODA GÖMÜLÜYDÜ ve düğme yine de basılabiliyordu:
 * kullanıcı uyarıyı okuyup yine deniyor, callable hata döndürüyordu. Üstelik
 * parçalar etiketlendiğinde metin yalan söylemeye başlayacaktı.
 *
 * Artık ön koşul her açılışta canlıdan okunur; etiketli parça yoksa düğme PASİF
 * olur ve sebebi yazar. Etiketleme yapılır yapılmaz düğme kendiliğinden açılır.
 */
const PANEL_TAG = "masif-panel";

/** Bir raporun ön koşul durumu. */
interface Readiness {
  loading: boolean;
  ready: boolean;
  /** Hazır değilse kullanıcıya gösterilecek sebep. */
  blockedReason?: string;
}

/** `masif-panel` etiketli parça var mı? Tek sorgu, tek kayıt. */
function useSolidPanelReadiness(enabled: boolean): Readiness {
  // Fabrika biçimi: `enabled` false iken dinleyici HİÇ AÇILMAZ (gereksiz okuma
  // ve yetkisiz sorgu olmaz).
  const { data, loading, error } = useCollection<{ id: string }>(
    () =>
      enabled
        ? query(collection(db, "components"), where("tags", "array-contains", PANEL_TAG), limit(1))
        : null,
    [enabled],
  );

  if (!enabled) return { loading: false, ready: false };
  if (loading) return { loading: true, ready: false };
  // Ön koşul OKUNAMADIYSA düğme kilitlenmez: teşhis aracı, kapı değil.
  if (error) return { loading: false, ready: true };
  if (data.length === 0) {
    return {
      loading: false,
      ready: false,
      blockedReason:
        `Katalogda "${PANEL_TAG}" etiketli hiçbir parça yok — rapor edilecek veri bulunmuyor. ` +
        "Parça Yönetimi → Etiketler'den masif panel parçalarını (ör. PANEL 18, PANEL 25, " +
        `PANEL 40) "${PANEL_TAG}" etiketiyle işaretleyin; düğme kendiliğinden etkinleşir.`,
    };
  }
  return { loading: false, ready: true };
}

const FINANCE_REPORTS = [
  {
    // ★ AD SUNUCUDAKİ EXPORT ADIYLA BİREBİR. `generateProfitReport` diye bir
    // fonksiyon yok; olmayan callable çağrısı Firebase'de `internal` döner ve
    // sunucuda hiçbir log bırakmaz — iki finansal rapor bu yüzden hiç
    // üretilemiyordu. Ad bir dizi içinde durduğu için otomatik tarayıcı da
    // görememişti.
    /** Rotanın son parçası — hangi raporun gösterileceğini bu belirler. */
    slug: "profit",
    callable: "exportProfitReport",
    title: "Ürün Kâr Tablosu",
    description:
      "SKU, ürün adı, maliyet, satış, brüt kâr ve kâr oranı. Parite eşiğinin altındaki ürünler işaretlenir.",
  },
  {
    slug: "solid-panel",
    callable: "exportSolidPanelReport",
    title: "Masif Panel Raporu",
    description:
      "Ürün başına masif panel m² tüketimi. Panel parçaları artık sabit ID ile değil, 'masif-panel' etiketiyle bulunur.",
    /**
     * ★ ÖN KOŞUL CANLIDAN OKUNUR — metin koda gömülü DEĞİL.
     * `useSolidPanelReadiness` etiketli parça yoksa düğmeyi pasif yapar ve
     * sebebi yazar. Eskiden metin sabitti ("0/312") ve düğme yine basılabiliyordu.
     */
    needsPanelTag: true,
  },
] as const;

/**
 * ★ İKİ ROTA ARTIK İKİ AYRI EKRAN.
 *
 * `App.tsx` hem `/reports/profit` hem `/reports/solid-panel` için AYNI
 * `<Reports />` bileşenini basıyor; kenar çubuğunda iki giriş vardı ama her
 * ikisi de birebir aynı sayfayı gösteriyordu — hangi menü öğesine basıldığı
 * hiçbir şeyi değiştirmiyordu. Rota yönlendirmesi `App.tsx` dışında çözülür
 * (o dosya bu görevin yazma izni dışında): sayfa kendi yolunu okur ve YALNIZCA
 * o rapora ait kartı basar. Bilinmeyen/eski yolda ikisi birden gösterilir.
 */
function activeSlug(pathname: string): string | null {
  const last = pathname.split("/").filter(Boolean).pop() ?? "";
  return FINANCE_REPORTS.some((r) => r.slug === last) ? last : null;
}

export default function Reports() {
  const can = useCan();
  const { pathname } = useLocation();
  const slug = activeSlug(pathname);

  // Yalnızca gösterilen rapor için ön koşul okunur.
  const shown = FINANCE_REPORTS.filter((r) => slug === null || r.slug === slug);
  const panelShown = can.seeFinance && shown.some((r) => "needsPanelTag" in r && r.needsPanelTag);
  const panelReadiness = useSolidPanelReadiness(panelShown);

  const heading =
    slug === null ? "Raporlar" : (FINANCE_REPORTS.find((r) => r.slug === slug)?.title ?? "Raporlar");

  return (
    <PageHeader title={heading}>
      {/*
        Kârlılık ve masif panel Excel'i YALNIZCA financeView. Yetki yoksa
        bölüm hiç render edilmez — buton da, callable çağrısı da olmaz.
      */}
      {can.seeFinance ? (
        <section className="mb-8">
          <h3 className="mb-1 text-base font-semibold text-slate-800">Finansal Raporlar</h3>
          {/*
            ★ METİN GERÇEĞE UYDURULDU. "İndirme bağlantısı 15 dakika geçerlidir"
            artık doğru değil: küçük raporlar imzalı URL ile değil, çağrı
            yanıtının içinde (base64) geliyor ve tarayıcıda yerel bir `blob:`
            adresine dönüşüyor. Ortada 15 dakika yaşayan bir bağlantı YOK —
            paylaşılabilir bir bağlantı hiç oluşmuyor.
          */}
          <p className="mb-3 text-xs text-slate-500">
            Maliyet ve kâr içerdikleri için yalnızca iş sahibine açıktır. Dosya çağrı yanıtının
            içinde gelir ve yalnızca bu sekmede yaşayan yerel bir bağlantıya dönüşür —{" "}
            <strong>paylaşılabilir bir indirme adresi üretilmez</strong>. Rapor her basılışta{" "}
            <strong>canlı veriden</strong> üretilir; önbellek kullanılmaz.
          </p>
          {/*
            ★ RAPORUN KAPSAMI ÖNCEDEN YAZAR.
            Kâr tablosu maliyeti ölçülmemiş ürünlerin kâr hücresini BOŞ bırakır
            (0 değil). Bu cümle olmadan boş hücre "kâr sıfır" gibi okunuyordu.
          */}
          <p className="mb-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <strong>Kapsam:</strong> Maliyeti ölçülmemiş ürünlerde kâr ve oran hücreleri{" "}
            <strong>boş</strong> gelir — sıfır değil, <em>ölçülmedi</em> demektir. Kâr oranı her
            zaman <strong>brüt kâr / net hasılat</strong> (KDV arındırılmış) tabanındadır.
          </p>
          <div className={`grid grid-cols-1 gap-4 ${shown.length > 1 ? "md:grid-cols-2" : ""}`}>
            {shown.map((r) => {
              const gated = "needsPanelTag" in r && r.needsPanelTag;
              return (
                <ReportCard
                  key={r.callable}
                  callableName={r.callable}
                  title={r.title}
                  description={r.description}
                  readiness={gated ? panelReadiness : undefined}
                />
              );
            })}
          </div>
        </section>
      ) : (
        <p className="mb-8 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
          Kârlılık ve masif panel raporları bu hesapta görüntülenemez. Operasyonel raporlar aşağıda
          listelenir.
        </p>
      )}

      <ReportArchive />
    </PageHeader>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// RAPOR ÜRETİMİ + 15 DAKİKALIK İMZALI URL
// ═══════════════════════════════════════════════════════════════════════════

function ReportCard({
  callableName,
  title,
  description,
  readiness,
}: {
  callableName: string;
  title: string;
  description: string;
  /**
   * Raporun ön koşulu. Verilmezse rapor her zaman çalıştırılabilir sayılır.
   * ★ Hazır değilken düğme PASİF olur — "uyarıyı yaz ama yine de bastır"
   * davranışı, kullanıcıyı bile bile hataya sürüklüyordu.
   */
  readiness?: Readiness;
}) {
  const run = useCallableAction<Record<string, never>, ReportOutput>(callableName);
  const [result, setResult] = useState<ReportOutput | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);

  // Sunucunun "veri yok" dalı: hata değil, bilgi. Dosya üretilmemiştir.
  const emptyInfo = result?.empty === true ? result : null;
  const fileReady = result !== null && result.empty !== true;

  const blocked = readiness !== undefined && !readiness.ready;
  const checking = readiness?.loading === true;

  /**
   * ★ DOSYA YANITIN İÇİNDE GELDİYSE TARAYICI BAĞLANTISINA ÇEVRİLİR.
   *
   * `blob:` adresi yalnızca bu sekmede yaşar, sunucuya hiç gitmez ve
   * paylaşılamaz — imzalı URL'den daha dar bir erişim. Sekme kapanınca ölür;
   * yine de bileşen kalkarken elle serbest bırakılır, aksi hâlde her rapor
   * üretiminde bellekte bir kopya birikirdi.
   */
  const blobUrl = useMemo(() => {
    const b64 = result?.contentBase64;
    if (!b64) return "";
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return URL.createObjectURL(
      new Blob([bytes], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
    );
  }, [result?.contentBase64]);

  useEffect(() => {
    if (!blobUrl) return;
    return () => URL.revokeObjectURL(blobUrl);
  }, [blobUrl]);

  // Bağlantının süresi dolduğunda kullanıcı bunu görsün; ölü linke tıklamasın.
  useEffect(() => {
    if (!result || result.empty === true) return;
    // Dosya yanıtın içinde geldiyse geri sayacak bir şey yok — bağlantı yerel.
    if (result.contentBase64) return;
    const expiry = Number(result.expiresAt);
    if (!Number.isFinite(expiry)) return;

    const tick = () => {
      const left = Math.floor((expiry - Date.now()) / 1000);
      setSecondsLeft(left > 0 ? left : 0);
      if (left <= 0) setResult(null);
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [result]);

  const mm = Math.floor(secondsLeft / 60);
  const ss = String(secondsLeft % 60).padStart(2, "0");

  return (
    <article className="rounded border border-emerald-300 bg-emerald-50/60 p-4">
      <h4 className="text-sm font-semibold text-emerald-900">{title}</h4>
      <p className="mt-1 text-xs text-emerald-800">{description}</p>
      {/* ★ ÖN KOŞUL SAĞLANMIYORSA SEBEP BURADA YAZAR — düğme de pasiftir. */}
      {blocked && readiness?.blockedReason ? (
        <p className="mt-2 rounded border border-amber-400 bg-amber-100 px-2 py-1.5 text-xs text-amber-900">
          <strong>Bu rapor şu anda üretilemez.</strong> {readiness.blockedReason}
        </p>
      ) : null}
      {checking ? (
        <p className="mt-2 text-xs text-slate-500">Ön koşullar denetleniyor…</p>
      ) : null}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={run.loading || blocked}
          title={blocked ? readiness?.blockedReason : undefined}
          onClick={async () => {
            const res = await run.run({});
            if (res) setResult(res);
          }}
          className="rounded bg-emerald-700 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {run.loading
            ? "Hazırlanıyor…"
            : checking
              ? "Denetleniyor…"
              : blocked
                ? "Veri yok — üretilemez"
                : "Excel Oluştur"}
        </button>

        {fileReady && result ? (
          <>
            <a
              href={blobUrl || result.url}
              download={result.fileName}
              rel="noopener noreferrer"
              className="rounded border border-emerald-700 bg-white px-3 py-2 text-sm font-medium text-emerald-800 hover:bg-emerald-100"
            >
              {result.fileName} indir
            </a>
            <span className="text-xs text-emerald-800">
              {n(result.rowCount)} satır
              {blobUrl
                ? " · dosya hazır, bu sayfadan indirilebilir"
                : ` · bağlantı ${mm}:${ss} sonra geçersiz olacak`}
            </span>
          </>
        ) : null}
      </div>

      {/*
        ★ GÜNCELLİK KANITI EKRANDA DA YAZAR.
        Kullanıcının sorusu "bu Excel şu an güncel mi?" idi. Yanıt iki ayrı
        tarihtir ve karıştırılmamalıdır: dosyanın basıldığı an ile VERİNİN
        tazelendiği an. Yalnızca ilki gösterilseydi, üç hafta önce hesaplanmış
        maliyetlerle basılmış bir dosya da "bugün" görünürdü.
      */}
      {fileReady && result && result.generatedAtMs ? (
        <dl className="mt-3 grid grid-cols-1 gap-x-4 gap-y-1 rounded border border-emerald-200 bg-white/70 px-3 py-2 text-xs text-emerald-900 sm:grid-cols-2">
          <div className="flex justify-between gap-2 sm:contents">
            <dt className="text-emerald-700">Rapor üretim anı</dt>
            <dd className="font-medium tabular-nums">{msToTr(result.generatedAtMs)}</dd>
          </div>
          <div className="flex justify-between gap-2 sm:contents">
            <dt className="text-emerald-700">Maliyetlerin son hesaplanması</dt>
            <dd className="font-medium tabular-nums">{msToTr(result.costsCalculatedAtMs)}</dd>
          </div>
          <div className="flex justify-between gap-2 sm:contents">
            <dt className="text-emerald-700">Fiyatı değişip maliyeti yenilenmemiş</dt>
            <dd
              className={`font-medium tabular-nums ${
                (result.staleCostCount ?? 0) > 0 ? "text-red-700" : ""
              }`}
            >
              {(result.staleCostCount ?? 0) === 0
                ? "yok — tüm satırlar güncel fiyatla"
                : `${n(result.staleCostCount ?? 0)} ürün`}
            </dd>
          </div>
        </dl>
      ) : null}

      {/*
        ★ SUNUCUNUN "VERİ YOK" YANITI HATA KUTUSUNDA GÖSTERİLMEZ.
        Rapor çalıştı, veri yoktu. Kullanıcının yapması gereken iş bellidir ve
        burada yazar; kırmızı hata kutusu "sistem bozuk" izlenimi veriyordu.
      */}
      {emptyInfo ? (
        <div className="mt-3 rounded border border-sky-300 bg-sky-50 px-3 py-2 text-xs text-sky-900">
          <p>
            <strong>Dosya üretilmedi — rapor edilecek veri yok.</strong> {emptyInfo.message}
          </p>
          {emptyInfo.candidates && emptyInfo.candidates.length > 0 ? (
            <p className="mt-1">
              Aday parçalar:{" "}
              {emptyInfo.candidates.map((c) => `${c.name} (${c.id})`).join(", ")}
            </p>
          ) : null}
        </div>
      ) : null}

      <ErrorNote error={run.error} />
    </article>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// RAPOR ARŞİVİ
// Görünürlük dokümandaki `allowedRoles` dizisinden gelir. Sorgu bu diziyi
// FİLTRELEMEK ZORUNDADIR — aksi hâlde Rules tüm listeyi reddeder.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * ★ DURUM ETİKETİ — BİLİNMEYEN DURUM ARTIK "Sırada" DEMEZ.
 *
 * ── ÖLÇÜLEN HATA (16.08.2026) ───────────────────────────────────────────────
 * Arşivdeki 4 raporun 4'ü de "Sırada" görünüyordu. Sebep: sunucu `status`
 * alanını HİÇ YAZMIYORDU (canlı ölçüm: type, allowedRoles, path, fileName,
 * rowCount, createdAt, createdBy — `status` yok, `title` yok). Firestore
 * olmayan alan için hata vermez, `undefined` döner; buradaki koşul zinciri de
 * `undefined` durumunda son dala düşüyor ve bitmiş raporu "kuyrukta" ilan
 * ediyordu. Sunucu artık `status: "ready"` yazıyor.
 *
 * Yine de `undefined` bir daha "Sırada" OLARAK YORUMLANMAZ: aynı hata tekrar
 * ederse ekran bunu gizlemek yerine "Bilinmiyor" diyerek görünür kılar.
 * Sessizce yanlış bir durum göstermektense bilinmediğini söylemek yeğdir.
 */
function statusLabel(status: ReportDoc["status"]): { label: string; color: string } {
  switch (status) {
    case "ready":
      return { label: "Hazır", color: "success" };
    case "error":
      return { label: "Hata", color: "danger" };
    case "running":
      return { label: "Üretiliyor", color: "warning" };
    case "queued":
      return { label: "Sırada", color: "neutral" };
    default:
      return { label: "Bilinmiyor", color: "neutral" };
  }
}

function ReportArchive() {
  const can = useCan();
  const role = can.role ?? "production";

  const { data, loading, error } = useCollection<ReportDoc>("reports", [
    where("allowedRoles", "array-contains", role),
    orderBy("createdAt", "desc"),
    limit(50),
  ]);

  return (
    <section>
      <h3 className="mb-1 text-base font-semibold text-slate-800">Rapor Arşivi</h3>
      {/*
        ★ METİN DÜRÜSTLEŞTİ. Eskiden "İndirmek için raporu yeniden oluşturun —
        eski bağlantılar kalıcı değildir" yazıyordu. Bu yanıltıcıydı: ölçüm
        (16.08.2026) Storage'da 7 rapor dosyasının HEPSİNİN durduğunu ve kova
        üzerinde silme (lifecycle) kuralı OLMADIĞINI gösterdi. Ölen şey dosya
        değil, yalnızca eski imzalı URL'lerdi. Dosyalar zaten `reports/finance/`
        altında `financeView` kilitli duruyor, dolayısıyla arşivden indirme
        Security Rules'un denetiminde doğrudan yapılabilir — kullanıcıyı
        raporu baştan ürettirmeye zorlamaya gerek yoktu.
      */}
      <p className="mb-3 text-xs text-slate-500">
        Yalnızca rolünüze açık raporlar listelenir. Dosyalar saklanır ve buradan{" "}
        <strong>doğrudan indirilebilir</strong>; indirme yetkisi her seferinde Storage
        kuralıyla denetlenir, paylaşılabilir bir bağlantı üretilmez.{" "}
        <strong>Arşiv dosyaları üretildikleri andaki verinin fotoğrafıdır</strong> — o
        tarihten sonra değişen fiyat ve maliyetleri içermez. Güncel rakamlar için raporu
        yukarıdan yeniden oluşturun.
      </p>

      <ErrorNote error={error} />
      {loading ? <Spinner /> : null}

      {!loading && data.length === 0 ? (
        <EmptyState title="Rapor yok" description="Henüz rapor üretilmemiş." />
      ) : null}

      {data.length > 0 ? (
        <div className="overflow-x-auto rounded border border-slate-200">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-100 text-left text-xs uppercase tracking-wide text-slate-600">
              <tr>
                <th className="px-3 py-2">Rapor</th>
                <th className="px-3 py-2">Tür</th>
                <th className="px-3 py-2 text-right">Satır</th>
                <th className="px-3 py-2">Durum</th>
                <th className="px-3 py-2">Oluşturulma</th>
                <th className="px-3 py-2">Maliyet tarihi</th>
                <th className="px-3 py-2">Dosya</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.map((r) => {
                const st = statusLabel(r.status);
                return (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 text-slate-800">{reportLabel(r)}</td>
                    <td className="px-3 py-2 text-slate-600">{r.type}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-600">
                      {r.rowCount ? n(r.rowCount) : "—"}
                    </td>
                    <td className="px-3 py-2">
                      <StatusBadge label={st.label} color={st.color} />
                      {r.errorMessage ? (
                        <span className="ml-2 text-xs text-red-700">{r.errorMessage}</span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-slate-500">{dt(r.createdAt ?? null)}</td>
                    {/*
                      Maliyet tarihi yalnızca yeni raporlarda vardır; eski
                      kayıtlar için "—" yazılır. Sıfır/boş tarih uydurulmaz.
                    */}
                    <td className="px-3 py-2 text-slate-500">
                      {r.costsCalculatedAt ? dt(r.costsCalculatedAt) : "—"}
                    </td>
                    <td className="px-3 py-2">
                      <ArchiveDownload report={r} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}

/**
 * ARŞİVDEN İNDİRME — imzalı URL YOK, kalıcı bağlantı YOK.
 *
 * ── NİÇİN CALLABLE, NİÇİN DOĞRUDAN STORAGE OKUMASI DEĞİL ───────────────────
 * İlk çözüm tarayıcıdan `getBytes()` ile doğrudan Storage okumaktı ve CANLIDA
 * ÖLÇÜLDÜ: çalışmıyor. Üst üste binen iki sebep vardı:
 *   1. `storage.rules` okuma kuralı `request.resource.contentType` denetliyordu.
 *      `request.resource` yalnızca YAZMA isteğinde doludur → kural iş sahibini
 *      bile reddediyordu. (Kural ayrıca düzeltildi.)
 *   2. Kovada CORS yapılandırması yok. Firebase SDK'nın indirme isteği
 *      `Authorization` başlığı taşıdığından ön uçuş (preflight) gerekir;
 *      yanıtsız kalınca SDK sessizce yeniden deniyor ve düğme "İndiriliyor…"
 *      durumunda ASILI KALIYORDU — kullanıcıya hata bile görünmüyordu.
 *
 * `downloadReport` callable'ı ikisini de gereksiz kılar ve rapor üretimiyle
 * AYNI modeli kullanır: dosya yanıtın içinde gelir, `blob:` adresine dönüşür,
 * ortada paylaşılabilir bir bağlantı hiç oluşmaz. Yetki her istekte sunucuda
 * yeniden denetlenir (rol + `allowedRoles` + finans yolunda `financeView`).
 */
interface DownloadOutput {
  fileName: string;
  sizeBytes: number;
  contentBase64: string;
}

function ArchiveDownload({ report }: { report: ReportDoc }) {
  const run = useCallableAction<{ reportId: string }, DownloadOutput>("downloadReport");

  // Yol yoksa indirilecek bir şey de yoktur; düğme yerine sebebi yazar.
  if (!report.path) {
    return <span className="text-xs text-slate-400">dosya yolu kayıtlı değil</span>;
  }

  const kb = report.sizeBytes ? ` · ${Math.round(report.sizeBytes / 1024)} KB` : "";

  return (
    <div>
      <button
        type="button"
        disabled={run.loading}
        onClick={async () => {
          const res = await run.run({ reportId: report.id });
          if (!res) return; // hata `run.error` üzerinden gösterilir
          const bin = atob(res.contentBase64);
          const bytes = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
          const url = URL.createObjectURL(
            new Blob([bytes], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            }),
          );
          const a = document.createElement("a");
          a.href = url;
          a.download = res.fileName;
          a.click();
          // Tarayıcı indirmeyi kuyruğa aldıktan sonra bırakılır; erken serbest
          // bırakmak indirmeyi yarıda kesebilir.
          window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
        }}
        className="rounded border border-emerald-700 bg-white px-2 py-1 text-xs font-medium text-emerald-800 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {run.loading ? "İndiriliyor…" : `İndir${kb}`}
      </button>
      {/* Sunucu hatası Türkçe ve sebebiyle gelir; sessiz başarısızlık bırakılmaz. */}
      {run.error ? <p className="mt-1 max-w-xs text-xs text-red-700">{run.error}</p> : null}
    </div>
  );
}
